import * as React from 'react';
import { Link } from 'react-router-dom';
import { RecentActivitiesButton } from '@/components/dashboard/recent-activities-panel';
import { Card, CardContent, CardHeader, CardTitle, DropdownSelect, ErrorState, PageHeader, Skeleton, SkeletonCard } from '@/components/ui';
import { api, ApiError } from '@/lib/api-client';
import { useI18n } from '@/lib/i18n';
import { LineChart } from '@/components/reports/report-charts';

type DashboardStats = {
  total: number;
  open: number;
  pending: number;
  resolved: number;
  unassigned: number;
  overdue: number;
  dueToday: number;
  awaitingResolutionConfirmation: number;
  awaitingUserReply: number;
  changeRequests: number;
};

type HourlyChart = { today: number[]; yesterday: number[] };
type GroupOption = { value: string; label: string };

type Translator = ReturnType<typeof useI18n>['t'];

function errorMessage(error: unknown, t: Translator): string {
  return error instanceof ApiError ? error.message : t('dashboard.loadError');
}

function MetricCard({ label, value }: { label: string; value: number }) {
  const { language } = useI18n();
  return (
    <Card className="h-full border-line shadow-card transition hover:-translate-y-0.5 hover:shadow-raised">
      <CardContent className="p-5">
        <p className="text-overline uppercase text-ink-subtle">{label}</p>
        <p className="mt-2 text-2xl font-bold tracking-tight text-ink">{value.toLocaleString(language)}</p>
      </CardContent>
    </Card>
  );
}

function useHourlyChart(groupId: string) {
  const { t } = useI18n();
  const [chart, setChart] = React.useState<HourlyChart>({ today: Array(24).fill(0), yesterday: Array(24).fill(0) });
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const requestRef = React.useRef<AbortController | null>(null);

  const load = React.useCallback(async () => {
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    setLoading(true);
    setError('');
    try {
      const response = await api.get<{ data: HourlyChart }>('/dashboard/chart', {
        params: { mode: 'hourly', ...(groupId ? { groupId } : {}) },
        signal: controller.signal,
      });
      if (!controller.signal.aborted) setChart(response.data);
    } catch (caught) {
      if (controller.signal.aborted || (caught instanceof Error && caught.name === 'AbortError')) return;
      setError(errorMessage(caught, t));
    } finally {
      if (requestRef.current === controller) {
        requestRef.current = null;
        if (!controller.signal.aborted) setLoading(false);
      }
    }
  }, [groupId, t]);

  React.useEffect(() => {
    void load();
    return () => requestRef.current?.abort();
  }, [load]);

  return { chart, error, loading, reload: load };
}

function TrendsChart({ chart }: { chart: HourlyChart }) {
  const { t } = useI18n();
  const labels = chart.today.map((_, hour) => `${String(hour).padStart(2, '0')}:00`);
  return (
    <LineChart
      labels={labels}
      series={[
        { key: 'today', label: t('dashboard.today'), values: chart.today, color: '#00368F' },
        { key: 'yesterday', label: t('dashboard.yesterday'), values: chart.yesterday, color: '#7CACF8' },
      ]}
      ariaLabel={t('dashboard.chartAria')}
    />
  );
}

function ticketHref(filter: string, groupId: string): string {
  const query = new URLSearchParams(filter);
  if (groupId) query.set('groupId', groupId);
  return `/tickets?${query.toString()}`;
}

export function DashboardPage() {
  const { t } = useI18n();
  const [stats, setStats] = React.useState<DashboardStats | null>(null);
  const [groups, setGroups] = React.useState<GroupOption[]>([]);
  const [groupId, setGroupId] = React.useState('');
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const requestRef = React.useRef<AbortController | null>(null);
  const { chart, error: chartError, loading: chartLoading, reload: reloadChart } = useHourlyChart(groupId);

  React.useEffect(() => {
    const controller = new AbortController();
    api.get<{ data: { groups: GroupOption[] } }>('/dashboard/groups', { signal: controller.signal })
      .then((response) => {
        if (!controller.signal.aborted) setGroups(response.data.groups);
      })
      .catch((caught) => {
        if (!(caught instanceof Error && caught.name === 'AbortError')) setGroups([]);
      });
    return () => controller.abort();
  }, []);

  const load = React.useCallback(async () => {
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    setLoading(true);
    setError('');
    try {
      const response = await api.get<{ data: DashboardStats }>('/dashboard/stats', {
        params: groupId ? { groupId } : undefined,
        signal: controller.signal,
      });
      if (!controller.signal.aborted) setStats(response.data);
    } catch (caught) {
      if (controller.signal.aborted || (caught instanceof Error && caught.name === 'AbortError')) return;
      setError(errorMessage(caught, t));
    } finally {
      if (requestRef.current === controller) {
        requestRef.current = null;
        if (!controller.signal.aborted) setLoading(false);
      }
    }
  }, [groupId, t]);

  React.useEffect(() => {
    void load();
    return () => requestRef.current?.abort();
  }, [load]);

  function retryAll() {
    void load();
    void reloadChart();
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-6">
          {Array.from({ length: 6 }).map((_, index) => <SkeletonCard key={index} />)}
        </div>
        <Card className="border-line shadow-card"><CardContent className="p-5"><Skeleton className="h-56 w-full" /></CardContent></Card>
      </div>
    );
  }

  if (error || !stats) return <ErrorState message={error || t('dashboard.dataUnavailable')} onRetry={retryAll} />;

  const cards = [
    { label: t('dashboard.overdue'), value: stats.overdue, filter: 'due=overdue' },
    { label: t('dashboard.dueToday'), value: stats.dueToday, filter: 'due=due_today' },
    { label: t('dashboard.open'), value: stats.open, filter: 'status=open' },
    { label: t('dashboard.awaitingResolutionConfirmation'), value: stats.awaitingResolutionConfirmation, filter: 'status=awaiting_resolution_confirmation' },
    { label: t('dashboard.awaitingUserReply'), value: stats.awaitingUserReply, filter: 'status=awaiting_user_reply' },
    { label: t('dashboard.changeRequests'), value: stats.changeRequests, filter: 'status=product_cr' },
  ];
  const groupOptions = [{ value: '', label: t('dashboard.allGroups') }, ...groups];

  return (
    <div className="animate-fade-in space-y-6">
      <PageHeader
        className="mb-0"
        title={t('dashboard.title')}
        description={t('dashboard.description')}
        actions={(
          <>
            <div className="w-56">
              <DropdownSelect options={groupOptions} value={groupId} onChange={setGroupId} placeholder={t('dashboard.allGroups')} />
            </div>
            <RecentActivitiesButton groupId={groupId || undefined} />
          </>
        )}
      />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-6">
        {cards.map((card) => (
          <Link key={card.filter} to={ticketHref(card.filter, groupId)} className="block rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2">
            <MetricCard label={card.label} value={card.value} />
          </Link>
        ))}
      </div>
      <Card className="border-line shadow-card">
        <CardHeader><CardTitle className="text-ink">{t('dashboard.trends')}</CardTitle><p className="text-sm font-normal text-ink-muted">{t('dashboard.trendsDescription')}</p></CardHeader>
        <CardContent>
          {chartLoading ? <Skeleton className="h-56 w-full" /> : chartError ? <ErrorState message={chartError} onRetry={() => void reloadChart()} /> : <TrendsChart chart={chart} />}
        </CardContent>
      </Card>
    </div>
  );
}

export default DashboardPage;
